import { Injectable } from '@angular/core';
import { Router, NavigationEnd, Params } from '@angular/router';
import { BrowserTab } from '@awesome-cordova-plugins/browser-tab/ngx';
import { Smartlook, SmartlookNavigationEvent, SmartlookViewState } from '@ionic-native/smartlook/ngx';
import { Platform, NavController } from '@ionic/angular';

import { FirebaseService } from '@services/firebase/firebase.service';
import { environment } from 'src/environments/environment';

const PAGES = {
  '/tabs/home': 'Home',
  '/home': 'Home',
  '/tabs/movements': 'Movements',
  '/movements': 'Movements',
  '/tabs/discover': 'Discover',
  '/discover': 'Discover',
  '/tabs/reads': 'Racional Reads',
  '/reads': 'Racional Reads',
  '/tabs/profile': 'Profile',
  '/profile': 'Profile',
  '/persistent-login': 'PersistentLogin',
  '/start': 'Start',
  '/investment-form': 'Initial Form',
  '/comming-soon': 'Comming Soon',
  '/login': 'Login',
  '/extended-investment-form': 'Extended Form',
  '/portfolio-creation': 'Portfolio Creation',
  '/deposit-info': 'Deposit Info',
  '/withdrawal-confirm': 'Withdrawal Confirm',
  '/contribution-strategy': 'Contribution Strategy',
  '/insert-referral-code': 'Insert Referral Code',
  '/invite-contacts': 'Invite Contacts',
  '/referrals': 'Referrals',
  '/help': 'Help',
  '/etfs-explained': 'Etfs Explained',
  '/blackrock': 'BlackRock',
  '/historic': 'Historic',
  '/markets': 'Market',
  '/buying-power': 'Buying Power',
  '/select-asset': 'Select asset',
  '/profile-settings': 'Settings',
  '/settings': 'More Settings',
  '/recurrent-buys': 'Recurrent Buys',
  '/subscription/plan-compare': 'Plan Compare',
  '/subscription/pro-details': 'Pro Details',
  '/subscription/my-subscription': 'My Pro Subscription',
  '/orders': 'Orders',
  '/search': 'Search',
  '/investments': 'Investments',
  '/my-data': 'My data',
};

const ROUTES_WITH_SWIPE_DISABLE = [
  'extended-investment-form',
  'initial-questionnaire/investment-form',
  'initial-questionnaire',
  'portfolio-creation',
  'recurrent-buy-create',
  'recurrent-buy-edit',
  'deposit-amount',
];

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  tabDoublePressListeners: { [key: string]: (() => void)[] };
  currentPage: string;

  constructor(
    private router: Router,
    private platform: Platform,
    private navCtrl: NavController,
    private browserTab: BrowserTab,
    private firebaseService: FirebaseService,
    private smartlook: Smartlook,
  ) {
    this.tabDoublePressListeners = {};
  }

  subscribeToNavigationAndUpdateSwipe(updateFn) {
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        updateFn(this.platform.is('ios') && !(ROUTES_WITH_SWIPE_DISABLE.some(route => event.url.includes(route))));
        this.smartlookTrackNavigation(this.currentPage, this.getPageName(event.url));
        this.currentPage = this.getPageName(event.url);
        this.firebaseService.setScreen(this.currentPage);
        this.styleScrollBars();
      }
    });
  }

  smartlookTrackNavigation(oldPage, newPage) {
    if (this.platform.is('cordova') && environment.production && !environment.isPartnerSite) {
      this.smartlook.trackNavigationEvent(new SmartlookNavigationEvent(oldPage, SmartlookViewState.STOP));
      this.smartlook.trackNavigationEvent(new SmartlookNavigationEvent(newPage, SmartlookViewState.START));
    }
  }

  tabDoublePress(tab: string) {
    if (this.tabDoublePressListeners[tab]) {
      this.tabDoublePressListeners[tab].forEach((callback) => callback());
    }
  }

  subscribeToTabDoublePress(tab: string, callback: () => void) {
    this.tabDoublePressListeners[tab] = [...(this.tabDoublePressListeners[tab] || []), callback];
    return () => {
      const removeIndex = this.tabDoublePressListeners[tab].indexOf(callback);
      this.tabDoublePressListeners[tab].splice(removeIndex, 1);
    };
  }

  getPageName(url: string): string {
    let pageName = PAGES[url];
    if (!pageName) {
      // tslint:disable:one-line
      if (url.includes('/portfolio-personalization')) {pageName = 'Portfolio Personalization'; }
      else if (url.includes('/markets?favoritesOnly=true')) {pageName = 'Favorites'; }
      else if (url.includes('/profile/edit')) {pageName = 'Edit Public Profile'; }
      else if (url.includes('/deposit-amount')) {pageName = 'Deposit Amount'; }
      else if (url.includes('/withdrawal-amount')) {pageName = 'Deposit Amount'; }
      else if (url.includes('/stocks-summary')) {pageName = 'Stocks Summary'; }
      else if (url.includes('/portfolio-summary')) {pageName = 'Portfolio Recommendation'; }
      else if (url.includes('/portfolio/')) {pageName = 'Portfolio'; }
      else if (url.includes('/recurrent-buy-create')) {pageName = 'Create recurrent buy'; }
      else if (url.includes('/recurrent-buy-edit')) {pageName = 'Edit recurrent buy'; }
      else if (url.includes('/send-stocks-info')) {pageName = 'Info Enviar Stocks'; }
      else if (url.includes('/select-asset?sendStocks=true')) {pageName = 'Seleccionar Stock a enviar'; }
      else if (url.includes('/select-contact')) {pageName = 'Seleccionar contacto'; }
      else if (url.includes('/movements/corporate-event')) {pageName = 'CorporateEvent'; }
      else if (url.includes('/movements/passive') && url.includes('type=dividends')) {pageName = 'Dividend'; }
      else if (url.includes('/movements/passive') && url.includes('type=commissions')) {pageName = 'Commission'; }
      else if (url.includes('/movements/') && url.includes('type=contribution')) {pageName = 'Deposit'; }
      else if (url.includes('/movements/') && url.includes('type=withdrawal')) {pageName = 'Withdrawal'; }
      else if (url.includes('/filter-movements') && url.includes('parentType=users')) {pageName = 'All User Movements'; }
      else if (url.includes('/filter-movements') && url.includes('parentType=portfolios')) {pageName = 'All Portfolio Movements'; }
      else if (url.includes('/asset-ranking/')) {
        const assetId = url.split('/asset-ranking/')[1].split('?')[0];
        pageName = `Asset Ranking ${assetId}`;
      } else if (url.includes('/asset-details/')) {
        const assetId = url.split('/asset-details/')[1].split('?')[0];
        pageName = `Asset Detail ${assetId}`;
      } else if (url.includes('/themes/')) {
        const themeId = url.split('/themes/')[1].split('?')[0];
        pageName = `Themes ${themeId}`;
      }
      else if (url.includes('/read/')) {
        const postId = url.split('/read/')[1].split('?')[0];
        pageName = `Reads Post ${postId}`.replace(/-/g, '_');
      }
      else if (url.includes('/campaign/')) {
        const campaignId = url.split('/campaign/')[1].split('?')[0];
        pageName = `Campaign ${campaignId}`;
      }
      else if (url.includes('/profile/')) {
        const profileId = url.split('/profile/')[1].split('?')[0];
        pageName = `Social Profile ${profileId}`;
      }
      else if (url.includes('/orders?')) {
        if (url.includes('side=B')) {
          if (url.includes('type=STOP')) { pageName = 'Orden STOP BUY'; }
          else if (url.includes('type=LIMIT')) { pageName = 'Orden LIMIT BUY'; }
          else if (url.includes('type=MARKET_IF_TOUCHED')) { pageName = 'Orden MARKET_IF_TOUCHED BUY'; }
        }
        else if (url.includes('side=S')) {
          if (url.includes('type=STOP')) { pageName = 'Orden STOP SELL'; }
          else if (url.includes('type=LIMIT')) { pageName = 'Orden LIMIT SELL'; }
          else if (url.includes('type=MARKET_IF_TOUCHED')) { pageName = 'Orden MARKET_IF_TOUCHED SELL'; }
        }
      }
      // tslint:enable:one-line
    }
    return pageName || '';
  }

  pop(): Promise<void> {
    return this.navCtrl.pop();
  }

  goBack(): void {
    this.navCtrl.back();
  }

  goTo(route: string | string[], queryParams?: Params, replaceUrl?: boolean): Promise<boolean> {
    return this.navCtrl.navigateForward(route, { queryParams, replaceUrl });
  }

  goToRoot(route: string, animation?: 'forward' | 'back'): Promise<boolean> {
    return this.navCtrl.navigateRoot(route, { animated: true, animationDirection: (animation || 'forward') });
  }

  openInBrowser(url: string) {
    if (this.platform.is('cordova')) {
      this.browserTab.openUrl(url);
    } else {
      this.openInNewTab(url);
    }
  }

  // For Browsers:

  // Leave the app and go to the route.
  // Cant come back to the app when pressing browser back button
  leaveTo(route: string): void {
    window.location.replace(route);
  }

  private openInNewTab(route: string): void {
    window.open(route, '_blank');
  }

  styleScrollBars() {
    const styles = document.createElement('style');
    styles.textContent = `
      /* width */
      ::-webkit-scrollbar {
        width: 8px;
      }
      /* Track */
      ::-webkit-scrollbar-track {
        background: var(--app-background);
      }
      /* Handle */
      ::-webkit-scrollbar-thumb {
        background: var(--color-grey-line);
      }
      /* Handle on hover */
      ::-webkit-scrollbar-thumb:hover {
        background: var(--color-grey-lightest);
      }
      @media (max-width: 570px) {
        ::-webkit-scrollbar { width: 0; }
        ::-webkit-scrollbar-track, ::-webkit-scrollbar-thumb, -webkit-scrollbar-thumb:hover {
          background: transparent;
        }
      }
    `;

    setTimeout(() => {
      const contents = document.querySelectorAll('ion-content');
      contents.forEach(c => c.shadowRoot.appendChild(styles.cloneNode(true)));
    }, 10);

    setTimeout(() => {
      const contents = document.querySelectorAll('ion-content');
      contents.forEach(c => c.shadowRoot.appendChild(styles.cloneNode(true)));
    }, 1000);

    setTimeout(() => {
      const contents = document.querySelectorAll('ion-content');
      contents.forEach(c => c.shadowRoot.appendChild(styles.cloneNode(true)));
    }, 5000);
  }

}
